import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Popover,
  TextField,
  Typography,
  Chip,
  Divider
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  Close as CloseIcon
} from '@mui/icons-material';

/**
 * DateRangeFilter - A common date range filter component with presets and custom date range.
 *
 * Props:
 *   - onChange({ startDate, endDate }): Called when a preset is selected or custom range is applied.
 *     startDate and endDate are YYYY-MM-DD strings.
 *   - defaultPreset: (optional) The initial preset to select. One of 'today', 'yesterday', 'thisMonth', 'ytd', 'custom'.
 *     Defaults to 'thisMonth'.
 *   - size: (optional) 'small' | 'medium'. Defaults to 'small'.
 *   - label: (optional) Label text. Defaults to 'Date Range'.
 *   - sx: (optional) Additional styling for the outer Box.
 */

// Helper: format a Date object to YYYY-MM-DD
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper: format YYYY-MM-DD to readable display string (e.g., "Feb 13, 2026")
const formatDisplay = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Preset definitions - exported so parents can compute initial dates
export const getPresetDates = (presetKey) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (presetKey) {
    case 'today': {
      const d = formatDate(today);
      return { startDate: d, endDate: d };
    }
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const d = formatDate(yesterday);
      return { startDate: d, endDate: d };
    }
    case 'thisMonth': {
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: formatDate(firstOfMonth), endDate: formatDate(today) };
    }
    case 'ytd': {
      const firstOfYear = new Date(today.getFullYear(), 0, 1);
      return { startDate: formatDate(firstOfYear), endDate: formatDate(today) };
    }
    case 'last7': {
      const d = new Date(today);
      d.setDate(d.getDate() - 7);
      return { startDate: formatDate(d), endDate: formatDate(today) };
    }
    case 'last30': {
      const d = new Date(today);
      d.setDate(d.getDate() - 30);
      return { startDate: formatDate(d), endDate: formatDate(today) };
    }
    case 'last90': {
      const d = new Date(today);
      d.setDate(d.getDate() - 90);
      return { startDate: formatDate(d), endDate: formatDate(today) };
    }
    case 'lastYear': {
      const d = new Date(today);
      d.setFullYear(d.getFullYear() - 1);
      return { startDate: formatDate(d), endDate: formatDate(today) };
    }
    default:
      return null;
  }
};

const PRESETS = [
  { key: 'today', label: 'Today', icon: <TodayIcon sx={{ fontSize: 16 }} /> },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'ytd', label: 'Year to Date' },
  { key: 'last7', label: 'Last 7 Days' },
  { key: 'last30', label: 'Last 30 Days' },
  { key: 'last90', label: 'Last 90 Days' },
  { key: 'lastYear', label: 'Last Year' },
  { key: 'custom', label: 'Custom Range', icon: <CalendarIcon sx={{ fontSize: 16 }} /> },
];

const DateRangeFilter = ({
  onChange,
  value, // Controlled preset key
  defaultPreset = 'thisMonth',
  size = 'small',
  label = 'Date Range',
  sx = {}
}) => {
  const [activePreset, setActivePreset] = useState(value || defaultPreset);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const triggerRef = useRef(null);

  // Sync with value prop if controlled
  useEffect(() => {
    if (value && value !== activePreset) {
      setActivePreset(value);
    }
  }, [value, activePreset]);

  // Get display text for the current selection
  const getDisplayText = () => {
    if (activePreset === 'custom') {
      if (customStart && customEnd) {
        return `${formatDisplay(customStart)} – ${formatDisplay(customEnd)}`;
      }
      return 'Custom Range';
    }
    const preset = PRESETS.find(p => p.key === activePreset);
    return preset ? preset.label : 'Select Date Range';
  };

  // Get subtitle dates for preset
  const getSubtitleText = () => {
    if (activePreset === 'custom') return '';
    const dates = getPresetDates(activePreset);
    if (!dates) return '';
    if (dates.startDate === dates.endDate) {
      return formatDisplay(dates.startDate);
    }
    return `${formatDisplay(dates.startDate)} – ${formatDisplay(dates.endDate)}`;
  };


  const handlePresetClick = (presetKey) => {
    if (presetKey === 'custom') {
      setActivePreset('custom');
      setAnchorEl(triggerRef.current);
      return;
    }

    setActivePreset(presetKey);
    setAnchorEl(null);
    const dates = getPresetDates(presetKey);
    if (dates && onChange) {
      onChange(dates, presetKey);
    }
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      setActivePreset('custom');
      setAnchorEl(null);
      if (onChange) {
        onChange({ startDate: customStart, endDate: customEnd }, 'custom');
      }
    }
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', ...sx }}>
      {/* Main trigger button */}
      <Box
        ref={triggerRef}
        onClick={handlePopoverOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: size === 'small' ? 0.85 : 1.25,
          border: '1px solid',
          borderColor: open ? 'primary.main' : '#D1D5DB',
          borderRadius: 1.5,
          cursor: 'pointer',
          backgroundColor: '#fff',
          transition: 'all 0.2s ease',
          minWidth: 220,
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: '#F9FAFB',
          },
          boxShadow: open ? '0 0 0 2px rgba(59, 130, 246, 0.15)' : 'none',
        }}
      >
        <DateRangeIcon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#6B7280',
              fontSize: '0.68rem',
              lineHeight: 1.2,
              display: 'block',
              letterSpacing: '0.02em',
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: '#111827',
              fontSize: '0.82rem',
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {getDisplayText()}
          </Typography>
          {getSubtitleText() && (
            <Typography
              variant="caption"
              sx={{
                color: '#9CA3AF',
                fontSize: '0.68rem',
                lineHeight: 1.2,
                display: 'block',
              }}
            >
              {getSubtitleText()}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'transform 0.2s ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Box>
      </Box>

      {/* Popover with presets and custom range */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.12), 0 2px 10px rgba(0,0,0,0.08)',
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
            minWidth: 280,
          },
        }}
      >
        {/* Preset buttons */}
        <Box sx={{ p: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '0.68rem',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              px: 1,
              mb: 0.5,
              display: 'block',
            }}
          >
            Quick Select
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {PRESETS.filter(p => p.key !== 'custom').map((preset) => {
              const dates = getPresetDates(preset.key);
              const isActive = activePreset === preset.key;
              return (
                <Box
                  key={preset.key}
                  onClick={() => handlePresetClick(preset.key)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    color: isActive ? '#fff !important' : '#374151',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: isActive ? 'primary.dark' : '#F3F4F6',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {preset.icon && (
                      <Box sx={{ color: isActive ? '#fff' : '#9CA3AF' }}>
                        {preset.icon}
                      </Box>
                    )}
                    <Typography
                      variant="body2"
                      color="inherit"
                      style={{ color: isActive ? '#fff' : 'inherit' }}
                      sx={{
                        fontWeight: isActive ? 600 : 500,
                        fontSize: '0.85rem',
                      }}
                    >
                      {preset.label}
                    </Typography>
                  </Box>
                  {dates && (
                    <Typography
                      variant="caption"
                      style={{ color: isActive ? 'rgba(255,255,255,0.8)' : '#9CA3AF' }}
                      sx={{
                        color: isActive ? 'rgba(255,255,255,0.7) !important' : '#9CA3AF',
                        fontSize: '0.72rem',
                        ml: 2,
                      }}
                    >
                      {dates.startDate === dates.endDate
                        ? formatDisplay(dates.startDate)
                        : `${formatDisplay(dates.startDate)} – ${formatDisplay(dates.endDate)}`
                      }
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Divider />

        {/* Custom Date Range */}
        <Box sx={{ p: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '0.68rem',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              px: 1,
              mb: 1,
              display: 'block',
            }}
          >
            Custom Date Range
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', px: 0.5, mb: 1.5 }}>
            <TextField
              type="date"
              label="Start Date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  fontSize: '0.85rem',
                },
              }}
            />
            <Typography variant="body2" sx={{ color: '#9CA3AF', flexShrink: 0 }}>to</Typography>
            <TextField
              type="date"
              label="End Date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  fontSize: '0.85rem',
                },
              }}
            />
          </Box>
          <Button
            variant="contained"
            fullWidth
            size="small"
            disabled={!customStart || !customEnd}
            onClick={handleCustomApply}
            style={{ color: '#fff' }}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              py: 0.8,
              boxShadow: 'none',
              color: '#fff !important',
              backgroundColor: 'primary.main',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: 'primary.dark',
                color: '#fff !important',
              },
              '&.Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.3) !important',
                backgroundColor: 'rgba(59, 130, 246, 0.5)',
              }
            }}
          >
            <span style={{ color: '#fff' }}>Apply Custom Range</span>
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default DateRangeFilter;
